import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, CheckCircle, Truck, XCircle, X, Radio, RotateCcw, DollarSign } from 'lucide-react';
import {
  fetchOrderById,
  confirmOrder,
  assignDriverToOrder,
  adminCancelOrder,
  refundOrder,
  refundUnavailableItems,
  fetchDrivers,
} from '../services/api.ts';
import './Orders.css';

const OrderDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [order, setOrder] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [liveConnected, setLiveConnected] = useState(false);
  const eventSourceRef = useRef<EventSource | null>(null);

  const [showDriverModal, setShowDriverModal] = useState(false);
  const [drivers, setDrivers] = useState<any[]>([]);
  const [selectedDriverId, setSelectedDriverId] = useState<number | ''>('');
  const [photoModal, setPhotoModal] = useState<{ url: string; itemName: string } | null>(null);

  const loadOrder = async () => {
    try {
      setLoading(true);
      const data = await fetchOrderById(Number(id));
      setOrder(data);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load order');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadOrder(); }, [id]);

  // SSE live updates for this specific order
  useEffect(() => {
    const token = localStorage.getItem('auth_token');
    if (!token || !id) return;
    const baseUrl = process.env.REACT_APP_API_URL || '';
    const url = `${baseUrl}/orders/live?token=${encodeURIComponent(token)}`;

    let es: EventSource | null = null;
    let retryTimeout: ReturnType<typeof setTimeout>;

    const connect = () => {
      es = new EventSource(url);
      eventSourceRef.current = es;
      es.onopen = () => setLiveConnected(true);
      es.onerror = () => {
        setLiveConnected(false);
        es?.close();
        retryTimeout = setTimeout(connect, 5000);
      };
      es.onmessage = (evt) => {
        try {
          const payload = JSON.parse(evt.data);
          if (payload.event === 'connected') return;
          if (payload.data?.id === Number(id)) {
            loadOrder();
          }
        } catch { /* ignore */ }
      };
    };

    connect();
    return () => {
      clearTimeout(retryTimeout);
      es?.close();
      eventSourceRef.current = null;
      setLiveConnected(false);
    };
  }, [id]);

  const handleConfirm = async () => {
    if (!window.confirm('Confirm this order?')) return;
    setActionLoading(true);
    try {
      await confirmOrder(Number(id));
      loadOrder();
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Failed to confirm order');
    } finally {
      setActionLoading(false);
    }
  };

  const handleCancel = async () => {
    if (!window.confirm('Cancel this order?')) return;
    setActionLoading(true);
    try {
      await adminCancelOrder(Number(id));
      loadOrder();
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Failed to cancel order');
    } finally {
      setActionLoading(false);
    }
  };

  const handleRefund = async () => {
    if (!window.confirm('Refund this order? This will reverse the payment and cancel the order.')) return;
    setActionLoading(true);
    try {
      await refundOrder(Number(id));
      loadOrder();
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Failed to refund order');
    } finally {
      setActionLoading(false);
    }
  };

  const handleRefundUnavailable = async () => {
    const unavailable = order?.items?.filter((i: any) => i.pickup_status === 'unavailable' && !i.item_refunded) || [];
    if (unavailable.length === 0) return;
    const amount = unavailable.reduce((sum: number, i: any) => sum + i.item_price * i.quantity, 0);
    const names = unavailable.map((i: any) => i.item_name).join(', ');
    if (!window.confirm(`Refund \u00a3${amount.toFixed(2)} for ${unavailable.length} unavailable item(s)?\n\n${names}`)) return;
    setActionLoading(true);
    try {
      const res = await refundUnavailableItems(Number(id));
      alert(res.message || 'Partial refund processed');
      loadOrder();
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Failed to process partial refund');
    } finally {
      setActionLoading(false);
    }
  };

  const openAssignDriver = async () => {
    try {
      const list = await fetchDrivers(0, 100);
      const zoneDrivers = list.filter((d: any) => d.zone?.id === order?.zone?.id && d.is_active);
      setDrivers(zoneDrivers);
      setSelectedDriverId('');
      setShowDriverModal(true);
    } catch {
      alert('Failed to load drivers');
    }
  };

  const handleAssignDriver = async () => {
    if (!selectedDriverId) return;
    setActionLoading(true);
    try {
      await assignDriverToOrder(Number(id), Number(selectedDriverId));
      setShowDriverModal(false);
      loadOrder();
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Failed to assign driver');
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="orders-page">
        <div className="orders-loading"><div className="orders-spinner" /><p>Loading order...</p></div>
      </div>
    );
  }
  if (error) return <div className="orders-page"><div className="orders-error">{error}</div></div>;
  if (!order) return null;

  const canConfirm = order.status === 'pending';
  const canAssignDriver = order.status === 'pending' || order.status === 'confirmed';
  const canCancel = !['delivered', 'cancelled'].includes(order.status);
  const canRefund = (order.payment_status === 'succeeded') && order.status !== 'delivered';
  const hasUnrefundedUnavailable = order.items?.some((i: any) => i.pickup_status === 'unavailable' && !i.item_refunded);
  const unavailableRefundAmount = order.items
    ?.filter((i: any) => i.pickup_status === 'unavailable' && !i.item_refunded)
    ?.reduce((sum: number, i: any) => sum + i.item_price * i.quantity, 0) || 0;

  return (
    <div className="orders-page">
      <div className="orders-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <button className="orders-btn orders-btn-secondary" onClick={() => navigate('/dashboard/orders')}>
            <ArrowLeft size={16} /> Back to Orders
          </button>
          <span className={`orders-live-badge ${liveConnected ? 'connected' : ''}`}>
            <Radio size={12} /> {liveConnected ? 'Live' : 'Connecting…'}
          </span>
        </div>
        <div className="orders-header-actions">
          {canConfirm && (
            <button className="orders-btn orders-btn-primary" onClick={handleConfirm} disabled={actionLoading}>
              <CheckCircle size={16} /> Confirm
            </button>
          )}
          {canAssignDriver && (
            <button className="orders-btn orders-btn-primary" onClick={openAssignDriver} disabled={actionLoading}>
              <Truck size={16} /> Assign Driver
            </button>
          )}
          {canCancel && (
            <button className="orders-btn orders-btn-danger" onClick={handleCancel} disabled={actionLoading}>
              <XCircle size={16} /> Cancel Order
            </button>
          )}
          {canRefund && (
            <button className="orders-btn orders-btn-warning" onClick={handleRefund} disabled={actionLoading}>
              <RotateCcw size={16} /> Refund
            </button>
          )}
          {hasUnrefundedUnavailable && ['succeeded', 'partially_refunded'].includes(order.payment_status) && (
            <button className="orders-btn orders-btn-warning" onClick={handleRefundUnavailable} disabled={actionLoading} style={{ background: '#f59e0b' }}>
              <DollarSign size={16} /> Refund Unavailable ({'\u00a3'}{unavailableRefundAmount.toFixed(2)})
            </button>
          )}
        </div>
      </div>

      {/* Order Header Card */}
      <div className="orders-card" style={{ marginBottom: '24px' }}>
        <div className="orders-detail-header">
          <div>
            <h2>Order {order.order_number}</h2>
            <span className={`orders-badge orders-badge-${order.status}`} style={{ fontSize: '14px', padding: '4px 12px' }}>
              {order.status?.replace('_', ' ')?.toUpperCase()}
            </span>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div className="orders-detail-amount">{'\u00a3'}{order.total_amount?.toFixed(2)}</div>
            <div className="orders-detail-payment">Payment: {order.payment_status || 'Not initiated'}</div>
            {order.refund_amount > 0 && (
              <div style={{ color: '#dc2626', fontSize: '13px', marginTop: '4px' }}>
                Refunded: {'\u00a3'}{order.refund_amount?.toFixed(2)}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="orders-detail-grid">
        {/* Customer */}
        <div className="orders-card">
          <h3>Customer</h3>
          <div className="orders-info-list">
            <div><strong>{order.customer?.name || '\u2014'}</strong></div>
            <div>{order.customer?.email || '\u2014'}</div>
            <div>{order.customer?.phone || '\u2014'}</div>
            <div className="orders-detail-payment">ID: {order.customer?.user_id}</div>
          </div>
        </div>

        {/* Delivery Address */}
        <div className="orders-card">
          <h3>Delivery Address</h3>
          <div className="orders-info-list">
            <div>{order.address?.line_1 || '\u2014'}</div>
            {order.address?.line_2 && <div>{order.address.line_2}</div>}
            <div>{order.address?.town || '\u2014'}</div>
            <div><strong>{order.address?.postcode || '\u2014'}</strong></div>
            <div>{[order.address?.state, order.address?.country].filter(Boolean).join(', ')}</div>
          </div>
        </div>

        {/* Driver & Zone */}
        <div className="orders-card">
          <h3>Assignment</h3>
          <div className="orders-info-list">
            <div><strong>Zone:</strong> {order.zone?.name || '\u2014'}</div>
            {order.driver ? (
              <>
                <div><strong>Driver:</strong> {order.driver.name}</div>
                <div><strong>Phone:</strong> {order.driver.phone || '\u2014'}</div>
                <div><strong>Vehicle:</strong> {order.driver.vehicle_type} {order.driver.vehicle_number ? `(${order.driver.vehicle_number})` : ''}</div>
              </>
            ) : (
              <div className="orders-detail-payment">No driver assigned</div>
            )}
          </div>
        </div>
      </div>

      {/* Order Items */}
      <div className="orders-card" style={{ marginBottom: '24px' }}>
        <h3>Items ({order.items?.length || 0})</h3>
        <table className="orders-items-table">
          <thead>
            <tr>
              <th>Item</th>
              <th>Retailer</th>
              <th>Price</th>
              <th>Qty</th>
              <th>Subtotal</th>
              <th>Pickup</th>
            </tr>
          </thead>
          <tbody>
            {order.items?.map((item: any) => (
              <tr key={item.id}>
                <td>
                  <strong>{item.item_name}</strong>
                  {item.pickup_status === 'replaced' && item.replacement_item_name && (
                    <div style={{ fontSize: '12px', color: '#2563eb', fontStyle: 'italic', marginTop: '2px' }}>
                      Replaced: {item.replacement_item_name}
                    </div>
                  )}
                  {item.pickup_notes && (
                    <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '2px' }}>{item.pickup_notes}</div>
                  )}
                </td>
                <td>{item.retailer_name || '\u2014'}</td>
                <td>{'\u00a3'}{item.item_price?.toFixed(2)}</td>
                <td>{item.quantity}</td>
                <td>{'\u00a3'}{(item.item_price * item.quantity).toFixed(2)}</td>
                <td>
                  {item.pickup_status ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <span style={{
                        display: 'inline-block',
                        padding: '2px 8px',
                        borderRadius: '12px',
                        fontSize: '11px',
                        fontWeight: 600,
                        background: item.pickup_status === 'collected' ? '#dcfce7'
                          : item.pickup_status === 'replaced' ? '#dbeafe'
                          : item.pickup_status === 'unavailable' ? '#fee2e2' : '#f3f4f6',
                        color: item.pickup_status === 'collected' ? '#166534'
                          : item.pickup_status === 'replaced' ? '#1e40af'
                          : item.pickup_status === 'unavailable' ? '#991b1b' : '#374151',
                      }}>
                        {item.pickup_status}
                      </span>
                      {item.item_refunded && (
                        <span style={{
                          display: 'inline-block',
                          padding: '2px 6px',
                          borderRadius: '12px',
                          fontSize: '10px',
                          fontWeight: 600,
                          background: '#fef3c7',
                          color: '#92400e',
                        }}>
                          refunded
                        </span>
                      )}
                      {item.photo_url && (
                        <img
                          src={item.photo_url}
                          alt="pickup"
                          onClick={() => setPhotoModal({ url: item.photo_url, itemName: item.item_name })}
                          style={{ width: '32px', height: '32px', objectFit: 'cover', borderRadius: '4px', border: '1px solid #e5e7eb', cursor: 'pointer' }}
                        />
                      )}
                    </div>
                  ) : (
                    <span style={{ color: '#9ca3af', fontSize: '12px' }}>Pending</span>
                  )}
                </td>
              </tr>
            ))}
            <tr className="orders-items-total">
              <td colSpan={5} style={{ textAlign: 'right' }}>Items Subtotal</td>
              <td>{'\u00a3'}{order.items?.reduce((sum: number, i: any) => sum + i.item_price * i.quantity, 0).toFixed(2)}</td>
            </tr>
            {order.delivery_fee > 0 && (
              <tr className="orders-items-total">
                <td colSpan={5} style={{ textAlign: 'right' }}>Delivery Fee</td>
                <td>{'\u00a3'}{order.delivery_fee?.toFixed(2)}</td>
              </tr>
            )}
            {order.surcharge_amount > 0 && (
              <tr className="orders-items-total">
                <td colSpan={5} style={{ textAlign: 'right' }}>Surcharge</td>
                <td>{'\u00a3'}{order.surcharge_amount?.toFixed(2)}</td>
              </tr>
            )}
            {order.discount_amount > 0 && (
              <tr className="orders-items-total">
                <td colSpan={5} style={{ textAlign: 'right', color: '#16a34a' }}>
                  Discount {order.coupon_code && <span style={{ fontSize: '11px', background: '#dcfce7', padding: '2px 6px', borderRadius: '4px', marginLeft: '4px' }}>{order.coupon_code}</span>}
                </td>
                <td style={{ color: '#16a34a' }}>-{'\u00a3'}{order.discount_amount?.toFixed(2)}</td>
              </tr>
            )}
            <tr className="orders-items-total" style={{ fontWeight: 700 }}>
              <td colSpan={5} style={{ textAlign: 'right' }}>Total</td>
              <td>{'\u00a3'}{order.total_amount?.toFixed(2)}</td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Store Arrivals */}
      {order.store_arrivals?.length > 0 && (
        <div className="orders-card" style={{ marginBottom: '24px' }}>
          <h3>Store Pickups</h3>
          <div className="orders-timeline">
            {order.store_arrivals.map((a: any, i: number) => (
              <div key={i} className="orders-timeline-item">
                <div className="orders-timeline-dot" />
                <div>
                  <span className="orders-timeline-label">{a.store_name} — {a.store_address}</span>
                  <span className="orders-timeline-time">
                    Driver: {a.driver || '\u2014'} · Arrived: {a.arrived_at ? new Date(a.arrived_at).toLocaleString() : '\u2014'}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Timeline */}
      <div className="orders-card">
        <h3>Timeline</h3>
        <div className="orders-timeline">
          {[
            { label: 'Created', time: order.timestamps?.created_at },
            { label: 'Confirmed', time: order.timestamps?.confirmed_at },
            { label: 'Assigned', time: order.timestamps?.assigned_at },
            { label: 'Picked Up', time: order.timestamps?.picked_up_at },
            { label: 'Delivered', time: order.timestamps?.delivered_at },
            { label: 'Cancelled', time: order.timestamps?.cancelled_at },
            { label: 'Refunded', time: order.timestamps?.refunded_at },
          ].filter(t => t.time).map((t, i) => (
            <div key={i} className="orders-timeline-item">
              <div className="orders-timeline-dot" />
              <div>
                <span className="orders-timeline-label">{t.label}</span>
                <span className="orders-timeline-time">{new Date(t.time).toLocaleString()}</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Photo Modal */}
      {photoModal && (
        <div className="orders-modal-overlay" onClick={() => setPhotoModal(null)} style={{ zIndex: 1000 }}>
          <div onClick={e => e.stopPropagation()} style={{ position: 'relative', maxWidth: '90vw', maxHeight: '90vh' }}>
            <button
              onClick={() => setPhotoModal(null)}
              style={{ position: 'absolute', top: '-12px', right: '-12px', background: '#fff', border: '1px solid #e5e7eb', borderRadius: '50%', width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', boxShadow: '0 2px 8px rgba(0,0,0,0.15)', zIndex: 1 }}
            >
              <X size={18} />
            </button>
            <div style={{ background: '#fff', borderRadius: '12px', padding: '16px', textAlign: 'center' }}>
              <h3 style={{ margin: '0 0 12px', fontSize: '16px' }}>{photoModal.itemName}</h3>
              <img
                src={photoModal.url}
                alt={photoModal.itemName}
                style={{ maxWidth: '80vw', maxHeight: '70vh', objectFit: 'contain', borderRadius: '8px' }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Assign Driver Modal */}
      {showDriverModal && (
        <div className="orders-modal-overlay" onClick={() => setShowDriverModal(false)}>
          <div className="orders-modal" onClick={e => e.stopPropagation()}>
            <div className="orders-modal-header">
              <h2>Assign Driver</h2>
              <button className="orders-modal-close" onClick={() => setShowDriverModal(false)}><X size={20} /></button>
            </div>
            <div className="orders-modal-body">
              {drivers.length === 0 ? (
                <p className="orders-empty" style={{ padding: '24px', textAlign: 'center' }}>No active drivers available in zone "{order.zone?.name}"</p>
              ) : (
                <div className="orders-form-row">
                  <label>Select Driver (Zone: {order.zone?.name})</label>
                  <select value={selectedDriverId} onChange={e => setSelectedDriverId(e.target.value ? Number(e.target.value) : '')}>
                    <option value="">Select a driver</option>
                    {drivers.map((d: any) => (
                      <option key={d.id} value={d.id}>
                        {d.first_name} {d.last_name} {d.is_available ? '(Available)' : '(Busy)'}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>
            <div className="orders-modal-footer">
              <button className="orders-btn orders-btn-secondary" onClick={() => setShowDriverModal(false)}>Cancel</button>
              <button className="orders-btn orders-btn-primary" onClick={handleAssignDriver} disabled={!selectedDriverId || actionLoading}>
                {actionLoading ? 'Assigning...' : 'Assign'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrderDetail;
